import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { InfiniteScroll } from "@/components/ui/infinite-scroll";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useWhatsAppUpdates } from "@/lib/useWhatsAppUpdates";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ChatPane, formatTime } from "@/components/whatsapp/ChatPane";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { WaAvatar } from "@/components/whatsapp/WaParts";
import {
  ArrowLeft,
  Ban,
  Briefcase,
  CheckCircle2,
  Info,
  Loader2,
  LockKeyhole,
  RotateCcw,
  Search,
  UserPlus,
  X,
  Zap,
  BarChart3,
} from "lucide-react";
import { QuickRepliesManager } from "./QuickRepliesManager";
import { WhatsAppReports } from "./WhatsAppReports";
import { dateRangeParams } from "@/lib/filters";
import type { WaConversation, WaConversationDetail } from "@/types/whatsapp";

type View = "all" | "unread" | "mine" | "unassigned" | "closed";

/** WhatsApp list time: "4:05 pm" today, "Yesterday", weekday this week, else date */
const listTime = (v: string | null) => {
  if (!v) return "";
  const d = new Date(v);
  if (d.toDateString() === new Date().toDateString()) return formatTime(v);
  if (d.toDateString() === new Date(Date.now() - 86400000).toDateString()) return "Yesterday";
  if (Date.now() - d.getTime() < 6 * 86400000) return d.toLocaleDateString("en-IN", { weekday: "long" });
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

export function WhatsAppPage() {
  const { hasPermission, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "inbox";
  const selectedId = params.get("c");
  const isAdmin = hasPermission("whatsapp.admin");
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();

  const [view, setView] = useState<View>("all");
  const [activity, setActivity] = useState("any");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [list, setList] = useState<WaConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listPageSize, setListPageSize] = useState(30);
  const [loadingMore, setLoadingMore] = useState(false);

  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("c", id);
    else next.delete("c");
    setParams(next, { replace: true });
  };
  const setTab = (t: string) => setParams(t === "inbox" ? {} : { tab: t }, { replace: true });

  const fetchChats = useCallback((pageToLoad: number, append: boolean) => {
    const qs = new URLSearchParams({ view, page: String(pageToLoad), ...dateRangeParams(activity, "date") });
    if (query) qs.set("search", query);
    return api.get(`/api/WhatsApp/Conversations?${qs}`).then((res) => {
      const paged = unwrapPage<WaConversation>(res);
      setList((prev) => {
        if (!append) return paged.rows;
        const ids = new Set(prev.map((c) => c.id));
        return [...prev, ...paged.rows.filter((c) => !ids.has(c.id))];
      });
      setListTotal(paged.total);
      setListPage(paged.page);
      setListPageSize(paged.page_size);
    });
  }, [view, query, activity]);

  const loadList = useCallback(() => {
    setLoading(true);
    fetchChats(1, false)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [fetchChats]);

  const loadMoreChats = () => {
    if (loadingMore || listPage * listPageSize >= listTotal) return;
    setLoadingMore(true);
    fetchChats(listPage + 1, true)
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    setLoading(true);
    loadList();
  }, [loadList]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/api/WhatsApp/Settings")
      .then((res) => setConnected(Boolean(unwrapObject<any>(res)?.connected)))
      .catch(() => undefined);
  }, [isAdmin]);

  useWhatsAppUpdates((e) => {
    loadList();
    if (!e || e.conversation_id === selectedId) setRefresh((n) => n + 1);
  }, tab === "inbox");

  const views: { key: View; label: string }[] = [
    { key: "all", label: roleCode === "SALES_HEAD" ? "Team" : "All" },
    { key: "unread", label: "Unread" },
    { key: "mine", label: "Mine" },
    { key: "unassigned", label: "Unassigned" },
    { key: "closed", label: "Closed" },
  ];

  const tabs = [
    { key: "inbox", label: "Chats", icon: null, show: true },
    { key: "quick", label: "Quick replies", icon: Zap, show: hasPermission("whatsapp.send") },
    { key: "reports", label: "Reports", icon: BarChart3, show: true },

  ].filter((t) => t.show);

  return (
    <div className="h-full flex flex-col p-3 sm:p-5 gap-3 w-full max-w-[1700px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <WhatsAppIcon className="size-8" />
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground leading-tight">WhatsApp</h1>
            <p className="text-xs text-muted-foreground">Company WhatsApp Business number</p>
          </div>
        </div>
        {tabs.length > 1 && (
          <div className="flex gap-1 rounded-full border bg-card p-1 text-sm">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-medium transition-colors",
                  tab === t.key ? "bg-[#25D366] text-white" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {t.icon ? <t.icon className="size-4" /> : <WhatsAppIcon className="size-4" color="currentColor" />}
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === "settings" && <Navigate to="/settings/whatsapp" replace />}
      {tab === "quick" && <QuickRepliesManager />}
      {tab === "reports" && <WhatsAppReports />}

      {tab === "inbox" && (
        <>
          {connected === false && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-300 flex flex-wrap items-center justify-between gap-2">
              <span>The company WhatsApp number isn't connected yet.</span>
              <button type="button" className="px-3 py-1 rounded-full bg-[#25D366] text-white text-xs font-semibold" onClick={() => navigate("/settings/whatsapp")}>
                Connect WhatsApp
              </button>
            </div>
          )}

          <div className="wa flex-1 min-h-[600px] flex rounded-xl overflow-hidden border shadow-sm" style={{ background: "var(--wa-panel)", borderColor: "var(--wa-border)" }}>
            {/* Chat list */}
            <aside
              className={cn("flex-col min-h-0 w-full md:w-[340px] lg:w-[380px] shrink-0 border-r", selectedId ? "hidden md:flex" : "flex")}
              style={{ borderColor: "var(--wa-border)", background: "var(--wa-panel)" }}
            >
              <div className="px-4 pt-3.5 pb-2">
                <h2 className="text-[22px] font-bold" style={{ color: "var(--wa-text)" }}>
                  Chats
                </h2>
              </div>
              <div className="px-3 pb-2">
                <div className="flex items-center gap-3 rounded-lg px-3 h-9" style={{ background: "var(--wa-header)" }}>
                  <Search className="size-4 shrink-0" style={{ color: "var(--wa-muted)" }} />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, number or deal"
                    className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-[var(--wa-muted)]"
                    style={{ color: "var(--wa-text)" }}
                  />
                  {search && (
                    <button type="button" onClick={() => setSearch("")} aria-label="Clear search" style={{ color: "var(--wa-muted)" }}>
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="px-3 pb-2">
                <select
                  value={activity}
                  onChange={(e) => setActivity(e.target.value)}
                  aria-label="Last activity"
                  className="w-full h-8 rounded-lg px-2.5 text-[13px] outline-none"
                  style={{ background: "var(--wa-header)", color: activity === "any" ? "var(--wa-muted)" : "var(--wa-text)" }}
                >
                  <option value="any">Last activity: any time</option>
                  <option value="today">Last activity: today</option>
                  <option value="last_7">Last activity: last 7 days</option>
                  <option value="this_month">Last activity: this month</option>
                  <option value="last_30">Last activity: last 30 days</option>
                </select>
              </div>
              <div className="flex gap-2 px-3 pb-2 overflow-x-auto">
                {views.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setView(v.key)}
                    className="px-3 py-1 rounded-full text-[14px] whitespace-nowrap transition-colors"
                    style={
                      view === v.key
                        ? { background: "var(--wa-pill-active)", color: "var(--wa-green-dark)" }
                        : { background: "var(--wa-header)", color: "var(--wa-muted)" }
                    }
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              <div className="wa-scroll flex-1 min-h-0 overflow-y-auto">
                {loading ? (
                  <div className="py-10 flex justify-center">
                    <Loader2 className="size-5 animate-spin" style={{ color: "var(--wa-green)" }} />
                  </div>
                ) : list.length === 0 ? (
                  <div className="py-12 px-6 text-center text-[14px]" style={{ color: "var(--wa-muted)" }}>
                    {view === "unread" ? "No unread chats" : "No chats here"}
                  </div>
                ) : (
                  list.map((c) => {
                    const active = selectedId === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => select(c.id)}
                        className="w-full flex items-center gap-3 pl-3 text-left transition-colors hover:bg-[var(--wa-hover)]"
                        style={active ? { background: "var(--wa-active)" } : undefined}
                      >
                        <WaAvatar size={49} />
                        <div className="flex-1 min-w-0 py-3 pr-3 border-b" style={{ borderColor: "var(--wa-border)" }}>
                          <div className="flex items-baseline gap-2">
                            <span className="text-[16px] truncate flex-1" style={{ color: "var(--wa-text)" }}>
                              {c.profile_name || c.phone}
                            </span>
                            <span
                              className="text-[12px] shrink-0"
                              style={{ color: c.unread_count ? "var(--wa-badge)" : "var(--wa-muted)", fontWeight: c.unread_count ? 500 : 400 }}
                            >
                              {listTime(c.last_message_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[14px] truncate flex-1" style={{ color: "var(--wa-muted)" }}>
                              {c.last_message_preview || c.phone}
                            </span>
                            {c.unread_count > 0 && (
                              <span
                                className="text-[12px] font-semibold min-w-5 h-5 px-1.5 rounded-full flex items-center justify-center text-white shrink-0"
                                style={{ background: "var(--wa-badge)" }}
                              >
                                {c.unread_count}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1 text-[11.5px]" style={{ color: "var(--wa-muted)" }}>
                            {c.deal && (
                              <span className="truncate max-w-[10rem] px-1.5 rounded" style={{ background: "var(--wa-header)" }}>
                                {c.deal.name}
                              </span>
                            )}
                            <span className="truncate">{c.assigned_user ? c.assigned_user.name : "Unassigned"}</span>
                            {c.conversation_status === "CLOSED" && <span>· Closed</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
                {list.length > 0 && (
                  <InfiniteScroll
                    hasMore={listPage * listPageSize < listTotal}
                    loading={loadingMore}
                    onLoad={loadMoreChats}
                  />
                )}
              </div>
            </aside>

            {/* Chat */}
            {selectedId ? (
              <ConversationView key={selectedId} conversationId={selectedId} refreshSignal={refresh} onBack={() => select(null)} onChanged={loadList} />
            ) : (
              <div className="hidden md:flex flex-1 flex-col items-center justify-center text-center px-10 border-b-[6px]" style={{ background: "var(--wa-header)", borderColor: "#25D366" }}>
                <WhatsAppIcon className="size-20 mb-6" color="#25D366" />
                <h2 className="text-[32px] font-light" style={{ color: "var(--wa-text)" }}>
                  WhatsApp for Nelgates CRM
                </h2>
                <p className="text-[14px] mt-3 max-w-md leading-relaxed" style={{ color: "var(--wa-muted)" }}>
                  Send and receive buyer messages from the company number. Chats are linked to contacts, leads and deals automatically.
                </p>
                <p className="text-[13px] mt-10 flex items-center gap-1.5" style={{ color: "var(--wa-muted)" }}>
                  <LockKeyhole className="size-3.5" /> Official WhatsApp Business API
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Chat header + chat + WhatsApp-style "Contact info" drawer */
function ConversationView({
  conversationId,
  refreshSignal,
  onBack,
  onChanged,
}: {
  conversationId: string;
  refreshSignal: number;
  onBack: () => void;
  onChanged: () => void;
}) {
  const { hasPermission, user } = useAuth();
  const { success, error } = useToast();
  const [detail, setDetail] = useState<WaConversationDetail | null>(null);
  const [users, setUsers] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [infoOpen, setInfoOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1700);
  const canSend = hasPermission("whatsapp.send");
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();
  const supervisor = roleCode === "SUPER_ADMIN" || roleCode === "SALES_HEAD";
  const myId = user?.Id;

  const load = useCallback(() => {
    api
      .get(`/api/WhatsApp/Conversations/${conversationId}`)
      .then((res) => setDetail(unwrapObject<WaConversationDetail>(res)))
      .catch(() => setDetail(null));
  }, [conversationId]);

  useEffect(load, [load, refreshSignal]);

  useEffect(() => {
    if (!supervisor) return;
    api
      .get("/api/User/Assignable")
      .then((res) => setUsers(unwrapArray(res)))
      .catch(() => setUsers([]));
  }, [supervisor]);

  const act = async (fn: () => Promise<any>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      if (ok) success(ok);
      load();
      onChanged();
    } catch (err: any) {
      error("Action failed", err.message);
    } finally {
      setBusy(false);
    }
  };

  const conv = detail?.conversation;
  const subtitle = conv
    ? [conv.phone, conv.assigned_user ? `Assigned to ${conv.assigned_user.name}` : "Unassigned"].join(" · ")
    : "";

  return (
    <>
      <section className="flex-1 min-w-0 flex flex-col min-h-0">
        <header className="h-[59px] shrink-0 flex items-center gap-3 px-4" style={{ background: "var(--wa-header)" }}>
          <button type="button" className="md:hidden -ml-1" onClick={onBack} aria-label="Back" style={{ color: "var(--wa-muted)" }}>
            <ArrowLeft className="size-5" />
          </button>
          <button type="button" className="flex items-center gap-3 min-w-0 flex-1 text-left" onClick={() => setInfoOpen((v) => !v)}>
            <WaAvatar size={40} />
            <div className="min-w-0">
              <div className="text-[16px] truncate" style={{ color: "var(--wa-text)" }}>
                {conv?.profile_name || conv?.phone || "…"}
              </div>
              <div className="text-[13px] truncate" style={{ color: "var(--wa-muted)" }}>
                {subtitle}
              </div>
            </div>
          </button>
          {conv?.opted_out && (
            <span className="text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1 text-white bg-[#ea0038]">
              <Ban className="size-3" /> Opted out
            </span>
          )}
          {conv && canSend && (
            <button
              type="button"
              disabled={busy}
              title={conv.conversation_status === "OPEN" ? "Close conversation" : "Reopen conversation"}
              onClick={() =>
                act(
                  () => api.patch(`/api/WhatsApp/Conversations/${conversationId}/${conv.conversation_status === "OPEN" ? "Close" : "Reopen"}`),
                  conv.conversation_status === "OPEN" ? "Conversation closed" : "Conversation reopened"
                )
              }
              className="size-10 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: "var(--wa-muted)" }}
            >
              {conv.conversation_status === "OPEN" ? <CheckCircle2 className="size-5" /> : <RotateCcw className="size-5" />}
            </button>
          )}
          <button
            type="button"
            title="Contact info"
            onClick={() => setInfoOpen((v) => !v)}
            className="size-10 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5"
            style={{ color: infoOpen ? "var(--wa-green)" : "var(--wa-muted)" }}
          >
            <Info className="size-5" />
          </button>
        </header>
        <ChatPane conversationId={conversationId} refreshSignal={refreshSignal} onChanged={() => (load(), onChanged())} className="flex-1" />
      </section>

      {/* Contact info drawer */}
      {infoOpen && (
        <aside className="hidden lg:flex flex-col w-[340px] shrink-0 border-l min-h-0" style={{ borderColor: "var(--wa-border)", background: "var(--wa-header)" }}>
          <div className="h-[59px] shrink-0 flex items-center gap-6 px-5" style={{ background: "var(--wa-panel)" }}>
            <button type="button" onClick={() => setInfoOpen(false)} aria-label="Close contact info" style={{ color: "var(--wa-muted)" }}>
              <X className="size-5" />
            </button>
            <span className="text-[16px]" style={{ color: "var(--wa-text)" }}>
              Contact info
            </span>
          </div>
          {!detail || !conv ? (
            <Loader2 className="size-5 animate-spin mx-auto mt-10" style={{ color: "var(--wa-green)" }} />
          ) : (
            <div className="wa-scroll flex-1 min-h-0 overflow-y-auto space-y-2.5 pb-4">
              <div className="flex flex-col items-center py-7 px-5" style={{ background: "var(--wa-panel)" }}>
                <WaAvatar size={150} />
                <div className="text-[22px] mt-4 text-center" style={{ color: "var(--wa-text)" }}>
                  {conv.profile_name || conv.phone}
                </div>
                <div className="text-[15px] mt-1" style={{ color: "var(--wa-muted)" }}>
                  {conv.phone}
                </div>
              </div>

              <InfoCard title="CRM record">
                {detail.contact ? (
                  <>
                    <div style={{ color: "var(--wa-text)" }}>{detail.contact.name}</div>
                    <div className="text-[13px]" style={{ color: "var(--wa-muted)" }}>
                      Contact{detail.contact.job_title ? ` · ${detail.contact.job_title}` : ""}
                      {detail.contact.account ? ` · ${detail.contact.account.name}` : ""}
                    </div>
                  </>
                ) : detail.lead ? (
                  <>
                    <div style={{ color: "var(--wa-text)" }}>{detail.lead.name}</div>
                    <div className="text-[13px]" style={{ color: "var(--wa-muted)" }}>
                      Lead{detail.lead.company_name ? ` · ${detail.lead.company_name}` : ""}
                      {detail.lead.lead_status ? ` · ${detail.lead.lead_status}` : ""}
                    </div>
                    <Link to={`/leads/${detail.lead.id}/edit`} className="text-[13px] hover:underline" style={{ color: "var(--wa-green)" }}>
                      Open lead
                    </Link>
                  </>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[13px]" style={{ color: "var(--wa-muted)" }}>
                      This number isn't saved in the CRM.
                    </p>
                    {canSend && hasPermission("lead.create") && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => act(() => api.post(`/api/WhatsApp/Conversations/${conversationId}/CreateLead`), "Lead created")}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-full text-white text-[14px] font-medium"
                        style={{ background: "var(--wa-green)" }}
                      >
                        <UserPlus className="size-4" /> Create lead
                      </button>
                    )}
                  </div>
                )}
              </InfoCard>

              <InfoCard title="Deal">
                {conv.deal ? (
                  <Link to={`/deals/${conv.deal.id}`} className="flex items-center gap-2 hover:underline" style={{ color: "var(--wa-green)" }}>
                    <Briefcase className="size-4" /> {conv.deal.name}
                  </Link>
                ) : (
                  <p className="text-[13px]" style={{ color: "var(--wa-muted)" }}>
                    Not linked to a deal.
                  </p>
                )}
                {canSend && detail.deals.length > 0 && (
                  <Select
                    value={conv.deal_id || "none"}
                    onValueChange={(v) =>
                      act(() => api.patch(`/api/WhatsApp/Conversations/${conversationId}/Link`, { deal_id: v === "none" ? null : v }), "Deal link updated")
                    }
                  >
                    <SelectTrigger className="h-9 text-[13px] mt-2 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No deal</SelectItem>
                      {detail.deals.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name} {d.deal_status !== "OPEN" ? `(${d.deal_status.toLowerCase()})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </InfoCard>

              <InfoCard title="Assigned to">
                {supervisor && canSend ? (
                  <Select
                    value={conv.assigned_user_id || "none"}
                    onValueChange={(v) =>
                      act(() => api.patch(`/api/WhatsApp/Conversations/${conversationId}/Assign`, { user_id: v === "none" ? null : v }), "Assignment updated")
                    }
                  >
                    <SelectTrigger className="h-9 text-[13px] w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name || ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="space-y-2">
                    <div style={{ color: "var(--wa-text)" }}>{conv.assigned_user?.name || "Unassigned"}</div>
                    {canSend && !conv.assigned_user_id && myId && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => act(() => api.patch(`/api/WhatsApp/Conversations/${conversationId}/Assign`, { user_id: myId }), "Assigned to you")}
                        className="w-full py-2 rounded-full text-white text-[14px] font-medium"
                        style={{ background: "var(--wa-green)" }}
                      >
                        Assign to me
                      </button>
                    )}
                  </div>
                )}
              </InfoCard>

              {conv.opted_out && (
                <InfoCard title="Messaging">
                  <p className="text-[13px] flex items-center gap-1.5" style={{ color: "#ea0038" }}>
                    <Ban className="size-4" /> Buyer replied STOP — templates are blocked.
                  </p>
                </InfoCard>
              )}
            </div>
          )}
        </aside>
      )}
    </>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-6 py-4 text-[15px]" style={{ background: "var(--wa-panel)" }}>
      <div className="text-[14px] mb-2" style={{ color: "var(--wa-muted)" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

export default WhatsAppPage;
